import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import ThemeToggle from "@/components/ThemeToggle";

export default function StudentSetPassword() {
  const navigate = useNavigate();
  const { student, mustChangePassword, clearMustChangePassword, refresh } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!student) navigate("/login", { replace: true });
  }, [student, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 6) return setError("Password must be at least 6 characters.");
    if (newPw !== confirm) return setError("New password and confirmation don't match.");
    setSubmitting(true);
    try {
      await api.post("/student/auth/set-password", { current_password: currentPw, new_password: newPw });
      clearMustChangePassword();
      await refresh();
      toast.success("Password updated. You're all set!");
      navigate("/student/dashboard", { replace: true });
    } catch (e) {
      setError(apiErr(e.response?.data?.detail) || "Could not update password");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-500 text-white flex items-center justify-center">
              <GraduationCap className="h-5 w-5" />
            </div>
            <span className="font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Trishul High School</span>
          </div>
          <ThemeToggle />
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-2 text-sky-700 dark:text-sky-300 mb-3">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em]">First-time setup</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Set a new password</h2>
          <p className="text-slate-500 mt-1 mb-5 text-sm">
            Hi {student?.name?.split(" ")[0] || "there"}! For security, please pick a new password before we continue.
            {mustChangePassword ? " Use your Student ID as the current password." : ""}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
            <div className="space-y-2">
              <Label htmlFor="cur">Current password {mustChangePassword && <span className="text-xs text-slate-400">(your Student ID)</span>}</Label>
              <Input id="cur" type={show ? "text" : "password"} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                className="h-11 rounded-xl" data-testid="set-pw-current"/>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New password</Label>
              <div className="relative">
                <Input id="new" type={show ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="h-11 rounded-xl pr-11" data-testid="set-pw-new"/>
                <button type="button" onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="conf">Confirm new password</Label>
              <Input id="conf" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="h-11 rounded-xl" data-testid="set-pw-confirm"/>
            </div>

            {error && <div data-testid="set-pw-error" className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2">{error}</div>}

            <Button type="submit" disabled={submitting} data-testid="set-pw-submit"
              className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600">
              {submitting ? "Saving…" : "Save & continue"}
            </Button>
          </form>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
